import { Row, Col } from "react-bootstrap";
import { FaFacebook, FaTwitter, FaInstagram, FaYoutube, FaTiktok, FaBandcamp, FaSpotify, FaPen } from "react-icons/fa";
import type { IconType } from "react-icons";
import { SiteContent } from "./content";
import { useSite } from "./content/context";
import { ActionButton } from "./edit/primitives";
import { compact } from "./edit/list";
import { safeHttpUrl } from "./urls";

type Social = keyof NonNullable<SiteContent["socials"]>;

const SOCIALS: { key: Social; label: string; Icon: IconType }[] = [
  { key: "facebook", label: "Facebook", Icon: FaFacebook },
  { key: "twitter", label: "X (Twitter)", Icon: FaTwitter },
  { key: "instagram", label: "Instagram", Icon: FaInstagram },
  { key: "youtube", label: "YouTube", Icon: FaYoutube },
  { key: "tiktok", label: "TikTok", Icon: FaTiktok },
  { key: "bandcamp", label: "Bandcamp", Icon: FaBandcamp },
  { key: "spotify", label: "Spotify", Icon: FaSpotify },
];

function Footer() {
  const { data, edit } = useSite();
  const socials = data.site.socials ?? {};

  const editLinks = async () => {
    if (!edit) return;
    const result = await edit.openForm({
      title: "Social links",
      fields: SOCIALS.map(({ key, label }) => ({
        name: key,
        label,
        type: "url" as const,
        hint: key === "spotify" ? "Leave a link empty to hide its icon." : undefined,
        validate: (v: string) => (!v || safeHttpUrl(v) ? undefined : "Enter a full link starting with https://"),
      })),
      values: { ...socials },
    });
    if (result && "values" in result) edit.update("site", { ...data.site, socials: compact(result.values) });
  };

  return (
    <footer className="af-footer py-3">
      <Row className="justify-content-center align-items-center">
        <Col xs="auto">
          {SOCIALS.map(({ key, label, Icon }) => {
            const href = safeHttpUrl(socials[key]);
            if (edit) {
              // In the editor, show every icon; missing ones are faded.
              return (
                <span key={key} className="mx-2 af-social" style={{ opacity: href ? 1 : 0.3 }} title={href ? label : `${label} (no link)`}>
                  <Icon size={30} />
                </span>
              );
            }
            return href ? (
              <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="mx-2 af-social" aria-label={label}>
                <Icon size={30} />
              </a>
            ) : null;
          })}
        </Col>
        {edit ? (
          <Col xs="auto">
            <ActionButton label="Edit social links" icon={<FaPen />} showLabel onClick={editLinks} />
          </Col>
        ) : null}
      </Row>
    </footer>
  );
}

export default Footer;
